/**
 * lettersStore — CRUD localStorage pour les lettres générées.
 *
 * Structure : liste de lettres avec metadata + emplacement (lettre_prete | stockage_final).
 * Aucune suppression automatique — l'utilisateur seul contrôle la suppression.
 * Zéro appel serveur.
 */

import { parseLetter } from "./parseLetter";

const KEY = "lexa_letters_v1";

const LOCATIONS = {
  READY: "lettre_prete",
  ARCHIVED: "stockage_final",
};

export { LOCATIONS };

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeAll(list) {
  localStorage.setItem(KEY, JSON.stringify(list));
}

export function listLetters() {
  return readAll().sort((a, b) => b.createdAt - a.createdAt);
}

export function listByLocation(location) {
  return listLetters().filter((l) => l.location === location);
}

/**
 * Ajoute (ou remplace) une lettre en position "Lettre prête".
 * Il n'y a qu'UNE seule lettre "prête" à la fois : la dernière remplace la précédente.
 * L'ancienne "lettre prête" n'est PAS supprimée automatiquement — elle reste dans
 * la liste mais avec location = orphan (utilisateur peut archiver ou supprimer).
 */
export function saveAsReady(letter) {
  const list = readAll();
  // Marquer les précédentes "prêtes" comme "à valider" (on les laisse à l'utilisateur)
  const cleaned = list.map((l) =>
    l.location === LOCATIONS.READY ? { ...l, location: LOCATIONS.READY } : l
  );

  const parsed = parseLetter(letter);
  const id =
    (crypto?.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const record = {
    id,
    createdAt: Date.now(),
    location: LOCATIONS.READY,
    title: parsed.title,
    preview: {
      object: parsed.object,
      recipient: parsed.recipient,
      date: parsed.date,
      excerpt: parsed.excerpt,
    },
    letter,
  };

  // On garde la précédente "prête" (elle devient obsolète mais on n'efface pas auto)
  cleaned.unshift(record);
  writeAll(cleaned);
  return record;
}

export function moveToArchive(id) {
  const list = readAll().map((l) =>
    l.id === id ? { ...l, location: LOCATIONS.ARCHIVED } : l
  );
  writeAll(list);
}

export function deleteLetter(id) {
  writeAll(readAll().filter((l) => l.id !== id));
}

export function renameLetter(id, newTitle) {
  const trimmed = (newTitle || "").trim();
  if (!trimmed) return;
  const list = readAll().map((l) =>
    l.id === id ? { ...l, title: trimmed } : l
  );
  writeAll(list);
}

export function getLetterById(id) {
  return readAll().find((l) => l.id === id);
}
